import React from "react"
import { Flex, Box } from "@rebass/grid/emotion"
import styled from "@emotion/styled"
import theme from "@nutrafol/nutrafol-ui-kit/styles/theme"
import { queries } from "@nutrafol/nutrafol-ui-kit/styles/mediaqueries"
import { segmentEvent } from "@nutrafol/nutrafol-ui-kit/utils/segmentfunctions"
import { ButtonWildMen } from "@nutrafol/nutrafol-ui-kit/dist/ButtonWildMen"
import { TextElementMen } from "@nutrafol/nutrafol-ui-kit/dist/TextElementMen"
import FaqItem from "./faqitem"
import { buildFAQPage } from "../../../../utils/schemaHelper"
import Helmet from "react-helmet"

const AccordionTitle = styled(Box)`
  ${queries.medium`
    text-align: center;
  `}
  &.top-border {
    ${queries.large`
    border-top: 1px solid ${theme.wild.color.master.base.alt.secondary};
  `}
  }
  @media (max-width: 767px) {
    h2 {
      font-size: 36px !important;
      line-height: 0.9 !important;
    }
  }
  .right {
    transform: none;
  }
`

const Card = styled(Box)`
  background: ${theme.color.beige};
  border-radius: 4px;
  padding: 32px 16px;
  @media (min-width: 768px) {
    padding: 56px 32px;
  }
  .card-description {
    ${queries.large`
      font-size: 16px !important;
    `}
  }
`

const ButtonWildWrap = styled(Box)`
  margin: 24px 0 8px 0;
  @media (min-width: 768px) {
    margin: 24px 0 24px 0;
  }
  button.textLink {
    text-transform: uppercase;
    @media (min-width: 1280px) {
      font-size: 14px;
      line-height: 20px;
    }
  }
  button.textLink.faq-cta.men-core {
    ${queries.medium`
      font-size: 14px !important;
    `}
  }
  .faq-link.men-core {
    ${queries.medium`
      font-size: 14px !important;
    `}
  }
`

const LinkWrap = styled(Box)`
  a {
    font-family: ${theme.wild.font.dinamo.regular};
    color: ${theme.wild.color.men.primary.default};
    text-transform: uppercase;
    border-bottom: 1px ${theme.wild.color.men.primary.default} solid;
    font-size: 12px;
    line-height: 16px;
    @media (min-width: 768px) {
      font-size: 14px;
      line-height: 20px;
    }
    &.faq-link.men-core {
      ${queries.medium`
        font-size: 14px !important;
      `}
    }
  }
`

const launchZenDesk = () => {
  segmentEvent("Start Chat Clicked", {
    text: "Start Chat",
    type: "button",
  })
  if (
    typeof window !== `undefined` &&
    typeof window.$zopim !== `undefined` &&
    typeof window.$zopim.livechat !== `undefined` &&
    typeof window.$zopim.livechat.window !== `undefined` &&
    typeof window.$zopim.livechat.window.show !== `undefined`
  ) {
    // show chat window
    window.$zopim.livechat.window.show()
  } else if (!!window.adaEmbed) {
    window.adaEmbed.toggle()
  }
}

const label = "See All FAQs"
const label2 = "More about our Services"

const FaqContact = ({ data, pageName }) => {
  const { heading, questions, pt, pb, hideFaqLink, hasTopBorder = true } = data

  return (
    <Box className="wild-container" pt={pt} pb={pb}>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(buildFAQPage(questions))}
        </script>
      </Helmet>
      <Box px={[16, 16, 0]}>
        <AccordionTitle
          pt={[16, null, 32, 96]}
          pb={hideFaqLink ? [26, null, 48, 96] : [16, null, 32, 64]}
          className={hasTopBorder ? "top-border" : "0"}
        >
          <Box maxWidth={[null, null, 700, 775]} mx="auto">
            <TextElementMen
              element="h2"
              text={heading}
              className="title--xlarge text-master-secondary-default"
            />
          </Box>
          {!hideFaqLink ? (
            <ButtonWildWrap>
              <ButtonWildMen
                textLink
                arrowRight
                className={`faq-cta ${pageName}`}
                label={label}
                ctaLink="/men/faq/"
                trackObject={{
                  type: "link",
                  destination: "/men/faq/",
                  module_location: "bottom_fold",
                  text: label,
                }}
              />
            </ButtonWildWrap>
          ) : null}
        </AccordionTitle>
        <FaqItem questions={questions} />
      </Box>

      <Flex
        flexDirection={["column", null, "row"]}
        mt={[32, null, null, null, 64]}
      >
        <Card width={[1, null, 0.5]} mr={[0, null, 8]} mb={[8, null, 0]}>
          <TextElementMen
            element="h2"
            text="WE’RE HERE FOR YOU."
            className="title--large md:title--medium lg:title--large text-men-secondary-secondary mb-3"
          />
          <TextElementMen
            element="p"
            text={`Chat with our customer support team. <br/>Monday - Friday ${process.env.GATSBY_CX_HOURS}`}
            className="body--large card-description mt-2"
          />
          <ButtonWildWrap>
            <ButtonWildMen
              textLink
              arrowRight
              label="Start Chat"
              onClick={launchZenDesk}
              className={`faq-link ${pageName}`}
            />
          </ButtonWildWrap>
          <TextElementMen
            element="p"
            text={`You can text or call us at:`}
            className="body--large mt-6 mb-1"
          />
          <LinkWrap>
            <a
              href="tel:8884543320"
              className={`faq-link ${pageName}`}
            >{`(888) 454-3320`}</a>
          </LinkWrap>
          <TextElementMen
            element="p"
            text={`Or reach us via email at:`}
            className="body--large mt-6 mb-1"
          />
          <LinkWrap>
            <a
              href="mailto:support@nutrafol.com"
              className={`faq-link ${pageName}`}
            >{`support@nutrafol.com`}</a>
          </LinkWrap>
        </Card>
        <Card width={[1, null, 0.5]}>
          <TextElementMen
            element="h2"
            text="Our service."
            className="title--large md:title--medium lg:title--large text-men-secondary-secondary mb-3"
          />
          <Box maxWidth={350}>
            <TextElementMen
              element="p"
              text="More than a supplement subscription, we’re committed to supporting you as you grow."
              className="body--large card-description mt-2"
            />
          </Box>
          <ButtonWildWrap>
            <ButtonWildMen
              textLink
              arrowRight
              label={label2}
              className={`faq-link ${pageName}`}
              ctaLink="/service/"
              trackObject={{
                type: "link",
                destination: "/service/",
                module_location: "bottom_fold",
                text: label2,
              }}
            />
          </ButtonWildWrap>
        </Card>
      </Flex>
    </Box>
  )
}

export default FaqContact
